// Global Free Gift Validation
(function() {
  let isUpdatingCart = false;

  function parseTimeToMinutes(value) {
    if (typeof value !== 'string') return null;
    const cleaned = value.trim();
    const match = cleaned.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;

    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

    return (hours * 60) + minutes;
  }

  function getCopenhagenMinutesNow() {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Europe/Copenhagen'
    });

    const parts = formatter.formatToParts(new Date());
    const hour = parseInt(parts.find(part => part.type === 'hour')?.value || '0', 10);
    const minute = parseInt(parts.find(part => part.type === 'minute')?.value || '0', 10);

    if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
    return (hour * 60) + minute;
  }

  function isGlobalGiftWindowActive() {
    const config = window.globalGiftSchedule || {};
    if (!config.enabled) return true;

    const start = parseTimeToMinutes(config.start);
    const end = parseTimeToMinutes(config.end);
    const now = getCopenhagenMinutesNow();

    if (start === null || end === null || now === null) return true;

    if (start <= end) {
      return now >= start && now <= end;
    }

    return now >= start || now <= end;
  }

  function setGiftInputsDisabled(disabled, scope) {
    const root = scope || document;
    const giftInputs = root.querySelectorAll('.global-gift-input');
    giftInputs.forEach(input => {
      if (disabled) {
        input.setAttribute('disabled', 'disabled');
      } else {
        input.removeAttribute('disabled');
      }
    });
  }

  function refreshCartUI(updatedCart, baseEvent, alreadyFetchedSection) {
    if (!updatedCart.sections || !updatedCart.sections['cart-drawer']) {
      if (alreadyFetchedSection) return;
      fetch('/?sections=cart-drawer')
        .then(res => res.json())
        .then(sectionData => {
          updatedCart.sections = sectionData && sectionData['cart-drawer']
            ? { 'cart-drawer': sectionData['cart-drawer'] }
            : (sectionData || {});
          refreshCartUI(updatedCart, baseEvent, true);
        })
        .catch(err => console.error('Error loading cart drawer section:', err));
      return;
    }

    const event = new CustomEvent('cart:updated', {
      detail: {
        cart: updatedCart,
        baseEvent: baseEvent
      }
    });

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer._onCartChanged) {
      cartDrawer._onCartChanged(event);
    } else if (window.location.pathname === '/cart') {
      window.location.reload();
    }
  }

  function checkGlobalGiftInCart() {
    if (isUpdatingCart) return Promise.resolve();

    return fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        // 0. Product-bound gifts: a line with property _gift_requires = <product id> is removed as soon as no
        //    (non-gift) line of that product is left in the cart. Used by the Apex pre-order (foam + cleaning tool).
        const boundGifts = cart.items.filter(item => item.properties && item.properties['_gift_requires']);
        if (boundGifts.length > 0) {
          const orphans = boundGifts.filter(gift => !cart.items.some(item =>
            !(item.properties && item.properties['_gift_requires']) &&
            String(item.product_id) === String(gift.properties['_gift_requires'])
          ));
          if (orphans.length > 0) {
            const updates = {};
            orphans.forEach(item => { updates[item.key] = 0; });
            isUpdatingCart = true;
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ updates, sections: ['cart-drawer'] })
            })
            .then(res => res.json())
            .then(newCart => {
              isUpdatingCart = false;
              refreshCartUI(newCart, 'custom:remove_bound_gift');
            })
            .catch(err => {
              console.error('Error removing product-bound gift:', err);
              isUpdatingCart = false;
            });
          }
        }

        const globalGiftWindowActive = isGlobalGiftWindowActive();

        // 1. Check for auto-remove logic
        // We want to auto-remove the global free gift if there are no other paid items left
        const hasPaidItems = cart.items.some(item => {
          return !(item.properties && item.properties['_global_free_gift']);
        });

        const globalGiftItems = cart.items.filter(item => {
          return item.properties && item.properties['_global_free_gift'];
        });

        if ((!hasPaidItems || !globalGiftWindowActive) && globalGiftItems.length > 0) {
          const updates = {};
          globalGiftItems.forEach(item => {
            updates[item.key] = 0;
          });

          isUpdatingCart = true;
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ updates, sections: ['cart-drawer'] })
          })
          .then(res => res.json())
          .then(newCart => {
            isUpdatingCart = false;
            refreshCartUI(newCart, !globalGiftWindowActive ? 'custom:remove_global_gift_outside_schedule' : 'custom:remove_global_gift');
          })
          .catch(err => {
            console.error('Error removing global gift:', err);
            isUpdatingCart = false;
          });
        }

        // 2. Enforce max 1 global gift item per order
        if (globalGiftItems.length > 0) {
          let totalGiftQty = 0;
          globalGiftItems.forEach(item => {
            totalGiftQty += item.quantity;
          });

          if (globalGiftItems.length > 1 || totalGiftQty > 1) {
            const updates = {};

            globalGiftItems.forEach((item, index) => {
              if (index === 0) {
                updates[item.key] = 1;
              } else {
                updates[item.key] = 0;
              }
            });

            isUpdatingCart = true;
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({ updates, sections: ['cart-drawer'] })
            })
            .then(res => res.json())
            .then(newCart => {
              isUpdatingCart = false;
              refreshCartUI(newCart, 'custom:normalize_global_gift');
            })
            .catch(err => {
              console.error('Error normalizing global gift quantity:', err);
              isUpdatingCart = false;
            });
          }
        }

        // 3. Prevent adding multiple logic
        const giftInputs = document.querySelectorAll('.global-gift-input');
        if (giftInputs.length === 0) return;

        const giftInCart = globalGiftItems.length > 0;
        setGiftInputsDisabled(giftInCart || !globalGiftWindowActive);
      })
      .catch(err => console.error('Error checking global gift:', err));
  }

  // Initial check
  document.addEventListener('DOMContentLoaded', checkGlobalGiftInCart);

  // Re-check on cart updates
  document.addEventListener('cart:updated', checkGlobalGiftInCart);
  document.addEventListener('cart:change', checkGlobalGiftInCart);
  document.addEventListener('cart:refresh', checkGlobalGiftInCart); // Some themes use this
  
  // Disable gift inputs before any add-to-cart is processed if outside scheduled window
  document.addEventListener('submit', function(e) {
    const form = e.target;
    if (!form || !form.matches || !form.matches('form')) return;

    const isProductSubmission = form.classList.contains('product-form') || form.getAttribute('action') === '/cart/add' || form.matches('form[is="product-form"]');
    if (!isProductSubmission) return;

    if (!isGlobalGiftWindowActive()) {
      setGiftInputsDisabled(true, form);
    }
  }, true);

  // Re-check after any form submission that might add to cart
  document.addEventListener('submit', function(e) {
    const form = e.target;
    if (form.classList.contains('product-form') || form.getAttribute('action') === '/cart/add' || form.matches('form[is="product-form"]')) {
       setTimeout(checkGlobalGiftInCart, 1500);
    }
  });
})();